/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { use, useEffect, useRef, useState } from 'react';
import { Modal, type ModalProps, View } from '@az/starc-ui';
import { useAppData } from '@/hooks/useAppData';
import { localStorage } from '@/utils/localStorage';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import styles from './styles.module.scss';
import { useSignInFlowType } from '../SignInFlowTypeContext';
import { useLabels } from '@/hooks/useLabels';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import dynamic from 'next/dynamic';
import { LoadingIndicator } from '@/components/AZCustomComponent/LoadingIndicator';
import {
  AccountViewProvider,
  AccountNotificationProvider,
  CreateAccountProvider,
  CreateAccountContext,
  RewardsProvider,
  AccountNotificationContext,
} from '@/features/account';
import { AccountViewContext } from '@/features/account/context/account_view_context';
import { routePaths } from '@/constants/routePaths';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { sessionStorage } from '@/utils/sessionStorage';
import { noBackOnAutoSubmit, noBackViews } from './constants';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type ViewProviderProps = {
  hideLoginSnackbar?: boolean;
  onClose?: (userAuthenticated?: boolean) => Promise<void> | void;
};

export default function SignInModalV2({
  hideLoginSnackbar,
  isOpen,
  onClose,
  autoSubmitEmail,
  id,
}: Props & ViewProviderProps) {
  const router = useLegacyRouter();
  return (
    <AccountViewProvider
      showHeaderAndFooterProp={router.route != '/checkout'}
      onCloseModal={onClose}
      hideLoginSnackbar={hideLoginSnackbar}
    >
      <AccountNotificationProvider>
        <CreateAccountProvider>
          <SignInModalV2Inner isOpen={isOpen} id={id} autoSubmitEmail={autoSubmitEmail} />
        </CreateAccountProvider>
      </AccountNotificationProvider>
    </AccountViewProvider>
  );
}

const LazyModalBody = dynamic(
  () => import('./components/ModalBody/ModalBody').then((mod) => mod.ModalBody),
  {
    ssr: false,
    loading: () => (
      <View direction="column" align="center" justify="center" attributes={{ style: { flex: 1 } }}>
        <LoadingIndicator />
      </View>
    ),
  },
);

type Props = {
  isOpen: boolean;
  id?: string;
  autoSubmitEmail?: string;
  disableCloseButton?: boolean;
};

type Size = ModalProps['size'];

const LABEL_KEYS = {
  forgotPassword: 'label_forgot_password_capitalized',
  label_MyAccountLanding_Rewards_Welcome: 'label_MyAccountLanding_Rewards_Welcome',
  label_streamline_signin_create_rewards_account: 'label_streamline_signin_create_rewards_account',
  label_streamline_signin_join_az_rewards: 'label_streamline_signin_join_az_rewards',
  label_close_modal: 'label_close_streamline_modal',
  label_back: 'label_back',
} as const;

function SignInModalV2Inner({ isOpen, id, autoSubmitEmail, disableCloseButton }: Props) {
  const { flowType, setFlowTypeFn, setRewardsStateFn } = useSignInFlowType();
  const { view, setView, reset, setUserName, setAutoSubmitEmail, onCloseModal } =
    use(AccountViewContext);
  const { reset: resetCreateAccount } = use(CreateAccountContext);
  const { onClose: onCloseNotification } = use(AccountNotificationContext);
  const enablePhoneSignIn = useForteDecisionFlagWithTrueDefault('enablePhoneSignIn');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const modalWidth = isTablet ? '542px' : '540px';
  const [closeButtonTitle, setCloseButtonTitle] = useState<string>();
  const labels = useLabels(LABEL_KEYS);
  const CLOSE_BUTTON_TEXT = 'modal dialog close';
  const isTabletRewards = flowType === 'rewardsEnterId' && isTablet;
  const isFullScreenModal = isMobile || isTabletRewards;
  const { appData } = useAppData();
  const { deviceType } = appData;
  const [showModalBody, setShowModalBody] = useState(isOpen);
  const isMX = useLocale() === countryCodes.mx;
  const tabletHorizontalPadding = 45 / 4;
  const { refetch: refetchHeaderData } = useHeaderData();
  const router = useLegacyRouter();
  const autoSubmittedEmail = useRef(false);

  if (autoSubmitEmail && !autoSubmittedEmail.current) {
    setAutoSubmitEmail(true);
    autoSubmittedEmail.current = true;
    setUserName(autoSubmitEmail);
  }

  const paddingBottom = () => {
    const starcModalContentPaddingPixels = 8;
    const mobileBrowserNavigationPixels = 54;
    const isPhone = deviceType === 'phone';

    if (isPhone) {
      return `${40 + mobileBrowserNavigationPixels - starcModalContentPaddingPixels}px`;
    } else {
      return '0px';
    }
  };

  useEffect(() => {
    switch (flowType) {
      case 'password':
        setCloseButtonTitle(
          `${labels.label_MyAccountLanding_Rewards_Welcome} ${CLOSE_BUTTON_TEXT}`,
        );
        return;
      case 'forgotPassword':
      case 'resetPasswordEmailSent':
        setCloseButtonTitle(`${labels.forgotPassword} ${CLOSE_BUTTON_TEXT}`);
        return;
      case 'manualCreateAccount':
        setCloseButtonTitle(
          `${labels.label_streamline_signin_create_rewards_account} ${CLOSE_BUTTON_TEXT}`,
        );
        return;
      case 'rewardsEnterId':
        setCloseButtonTitle(
          `${labels.label_streamline_signin_join_az_rewards} ${CLOSE_BUTTON_TEXT}`,
        );
        return;
      default:
        setCloseButtonTitle(
          isMX
            ? labels.label_close_modal
            : `${labels.label_streamline_signin_join_az_rewards} ${CLOSE_BUTTON_TEXT}`,
        );
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowType]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    const MODAL_HIDE_DELAY = 300;

    if (isOpen) {
      sessionStorage.setItem('signInModalOpened', 'true');
      setShowModalBody(true);
    } else {
      timeout = setTimeout(() => setShowModalBody(false), MODAL_HIDE_DELAY);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isOpen]);

  const flowTypeRef = useRef('login');
  useEffect(() => {
    flowTypeRef.current = flowType;
  }, [flowType]);

  const triggerDelayedSnackBar = () => {
    document.dispatchEvent(new CustomEvent('delayedSignInSnackBar', {}));
    localStorage.removeItem('delaySignInSnackBar');
  };

  const handleCloseModal = async () => {
    if (disableCloseButton) {
      return;
    }
    triggerDelayedSnackBar();
    if (
      flowTypeRef.current === 'signInOrCreateNewPW' ||
      ['keep_current_password', 'create_new_password'].includes(view)
    ) {
      if ([routePaths.cart, routePaths.rewardsLandingPage].includes(router.route)) {
        window.location.reload();
        return;
      }
      await refetchHeaderData();
    }
    await onCloseModal?.(false);
    setTimeout(() => {
      setView('login');
      reset();
      onCloseNotification();
      resetCreateAccount();
      setRewardsStateFn('initial');
      setFlowTypeFn('login');
    }, 500);
  };

  const headerColorValue = isMobile && flowType !== 'rewardsEnterId' ? 'primary' : 'secondary';

  const handleOnClick = () => {
    onCloseNotification();
    switch (view) {
      case 'login':
        return (window.location.href = '/');
      case 'password':
      case 'sms_otp_code':
      case 'auto_sms_otp_code':
      case 'auto_email_otp_code':
      case 'email_otp_code':
        setFlowTypeFn('login');
        setView('login');
        reset();
        break;
      case 'validate_otp_code_sms':
        if (enablePhoneSignIn) {
          setView('login');
          reset();
          resetCreateAccount();
        } else {
          setView('sms_otp_code');
        }
        break;
      case 'validate_otp_code':
        if (enablePhoneSignIn) {
          setView('login');
          reset();
          resetCreateAccount();
        } else {
          setView('email_otp_code');
        }
        break;
      case 'auto_validate_sms_otp_code':
        if (enablePhoneSignIn) {
          setView('login');
          reset();
          resetCreateAccount();
        } else {
          setView('auto_sms_otp_code');
        }
        break;
      case 'auto_validate_email_otp_code':
        if (enablePhoneSignIn) {
          setView('login');
          reset();
          resetCreateAccount();
        } else {
          setView('auto_email_otp_code');
        }
        break;
      case 'forgot_password':
        setView('password');
        break;
      case 'forgot_password_sms':
        setView('password');
        break;
      case 'validate_forgot_password':
        setView('forgot_password');
        break;
      case 'validate_forgot_password_sms':
        setView('forgot_password_sms');
        break;
      case 'auto_create_new_password':
      case 'create_account':
        setView('login');
        reset();
        resetCreateAccount();
        break;
      default:
        window.location.href = '/';
        break;
    }
  };

  const showBackButton = () => {
    if (autoSubmitEmail && noBackOnAutoSubmit.has(view)) {
      return false;
    }

    if (noBackViews.has(view)) {
      return false;
    }

    if (view === 'auto_create_new_password') {
      return !isMobile;
    }
    return true;
  };

  const [modalSize, setModalSize] = useState<Size>();

  useEffect(() => {
    let s: Size, m: Size, l: Size;
    s = 'full';
    m = undefined;
    l = undefined;
    if (flowType === 'rewardsEnterId') {
      m = 'full';
    }
    setModalSize({ s, m, l });
  }, [flowType]);

  return (
    <RewardsProvider>
      <Modal
        headerColor={headerColorValue}
        size={modalSize}
        attributes={{
          style: {
            maxWidth: isFullScreenModal ? '100%' : modalWidth,
            maxHeight: isFullScreenModal ? '100%' : '890px', // max height speficied by Rashad
            overflowY: 'auto',
            height: isFullScreenModal ? '' : 'auto',
            margin: 'auto',
            paddingBottom: paddingBottom(),
            scrollbarWidth: 'none',
          },
        }}
        closeButtonTitle={closeButtonTitle}
        id={id}
        open={isOpen}
        hasCloseButton={!(isMobile && flowType === 'rewardsEnterId')}
        className={styles.signInModalV2}
        onClose={handleCloseModal}
        hasHeaderBackButton={showBackButton()}
        backButtonTitle={labels.label_back}
        onBackClick={handleOnClick}
      >
        <View
          width="100%"
          direction="column"
          padding={{
            s: [0, 0, 40 / 4, 0],
            m: [18 / 4, tabletHorizontalPadding, 60 / 4, tabletHorizontalPadding],
            l: [6 / 4, 45 / 4, 45 / 4, 45 / 4],
          }}
        >
          {isOpen || showModalBody ? <LazyModalBody /> : null}
        </View>
      </Modal>
    </RewardsProvider>
  );
}
